// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@lfx/shared/shared.module';
import { BarEchartsComponent } from '@lfx/shared/components/charts/bar-echarts/bar-echarts.component';
import { NoDataChartComponent } from './no-data-chart.component';
import { LoadingSkeletonChartComponent } from './loading-skeleton-chart.component';

@Component({
  selector: 'lfx-influence-chart',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    BarEchartsComponent,
    NoDataChartComponent,
    LoadingSkeletonChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      .chart-container {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }

      .chart-mini {
        height: var(--lf-tech-chart-height);
        width: 100%;
      }

      .chart-large {
        height: 100%;
        width: 100%;
      }
    `,
  ],
  template: `
    <div
      class="chart-container"
      [class.chart-mini]="size() === 'mini'"
      [class.chart-large]="size() === 'large'"
    >
      @switch (chartData()?.chartType || 'no-data') {
        @case ('bar') {
          <lfx-bar-echarts
            [sparkline]="size() === 'mini'"
            [seriesData]="firstSeries() || []"
            [xAxisLabels]="chartData()?.xAxisLabels || []"
            [hideLegend]="true"
          >
          </lfx-bar-echarts>
        }
        @case ('stacked-bar') {
          <lfx-bar-echarts
            [sparkline]="size() === 'mini'"
            [seriesData]="chartData()?.multiSeriesData || []"
            [xAxisLabels]="chartData()?.xAxisLabels || []"
            [hideLegend]="true"
          >
          </lfx-bar-echarts>
        }
        @case ('line') {
          <lfx-line-echarts
            [sparkline]="size() === 'mini'"
            [seriesData]="firstSeries() || []"
            [xAxisLabels]="chartData()?.xAxisLabels || []"
            [hideLegend]="true"
          >
          </lfx-line-echarts>
        }
        @case ('area') {
          <lfx-line-echarts
            [sparkline]="size() === 'mini'"
            [seriesData]="chartData()?.multiSeriesData || []"
            [xAxisLabels]="chartData()?.xAxisLabels || []"
            [hideLegend]="true"
          >
          </lfx-line-echarts>
        }
        @case ('loading') {
          <lfx-loading-skeleton-chart></lfx-loading-skeleton-chart>
        }
        @case ('no-data') {
          <lfx-no-data-chart [iconType]="chartData()?.iconType"></lfx-no-data-chart>
        }
        @default {
          <lfx-no-data-chart [iconType]="chartData()?.iconType"></lfx-no-data-chart>
        }
      }
    </div>
  `,
})
export class InfluenceChartComponent {
  readonly chartData = input.required<{
    multiSeriesData: {
      data: number[];
      label?: string;
      dashArray?: number[];
    }[];
    xAxisLabels?: string[];
    chartType?: 'bar' | 'stacked-bar' | 'line' | 'area' | 'no-data' | 'loading';
    iconType?: 'eyes' | 'no-signal' | undefined;
  }>();
  readonly size = input<'mini' | 'large'>('mini');

  readonly firstSeries = computed(() => this.getFirstSeries(this.chartData()?.multiSeriesData || []));

  private getFirstSeries(multiSeriesData: {
    data: number[];
    label?: string;
    dashArray?: number[];
  }[]): {
    data: number[];
    label?: string;
    dashArray?: number[];
  }[] {
    return [multiSeriesData[0]];
  }
}
